import { useEffect, useState } from 'react'
import styled from 'styled-components';
import { Button } from '@mui/material';
import { TLineDetails } from '../../Models/Inbound';
import Title from '../custom/Title';
import DynamicForm, { TField, getFormConfigDeepCopy, useDynamicForm } from '../forms/DynamicFormNew';
import useService from '../../hooks/useService';
import { createOrUpdateLineDetails } from '../../services/Transactions';
import { FLOAT_REGEX, LINE_PRODUCT_TYPE, METAL_TYPES, MMTC_PRODUCT_NONMELTED_LIMIT, TRANSACTION_TYPES } from '../../utils/constant';
import { getThreePointNumber, getUserPrivilages } from '../../utils/helpers';


const Divider = styled.div`
    border: 1px solid #EBEBEC;
`

export const CONFIG: TField<TLineDetails>[] = [
    {
        fieldName: 'declaredWeight',
        isVisible: true,
        fieldType: 'text',
        label: 'Declared Weight',
        width: 'calc(33% - 20px)',
        decimalPlaces:3,
        regexFilter: FLOAT_REGEX,
        validation: {
            required: true,
        }
    },
    {
        fieldName: 'productType',
        isVisible: true,
        fieldType: 'select',
        label: 'Product Type',
        width: 'calc(33% - 20px)',
        options: Object.entries(LINE_PRODUCT_TYPE).map(option => ({ label: option[0], value: option[1] })),
        validation: {
            required: true
        }
    },
    {
        fieldName: 'netWeight',
        isVisible: true,
        disabledManualInput:true,
        fieldType: 'text',
        label: 'Net Weight',
        width: 'calc(33% - 20px)',
        decimalPlaces:3,
        regexFilter: FLOAT_REGEX,
        validation: {
            required: true
        }
    },
    {
        fieldName: 'metalType',
        disabled:true,
        fieldType: 'select',
        isVisible: false,
        label: 'Metal Type',
        width: 'calc(33% - 20px)',
        options: [
            { label: 'Gold', value: METAL_TYPES.GOLD },
            { label: 'Silver', value: METAL_TYPES.SILVER },
        ]
    }
]

function InboundLine({ lineId, closeInboundLine, showSaveButton }: { lineId?: string, closeInboundLine: () => any, showSaveButton: boolean }) {
    const { execute, store } = useService()
    const { transactionsStore, notificationStore, authStore } = store
    const { lineDetails, setLineDetails: setLineItems, transactionInfo: { globalTransactionNumber, transactionType }, inboundDetails } = transactionsStore
    const { notify } = notificationStore
    const { user } = authStore
    const [manualInput, setManualInput] = useState(false)
    const [renderCount, setRenderCount] = useState(0)

    const privilages = getUserPrivilages(user)

    const { registerForm, setFormData, setFormConfig, setField, formData } = useDynamicForm({
        formConfig: getFormConfigDeepCopy(CONFIG)
    })

    const buybackAssuredCase = () => formData.productType === LINE_PRODUCT_TYPE['MMTC-PAMP Product'] && transactionType === TRANSACTION_TYPES.BUY_BACK

    const createInboundLine = async (newLine: TLineDetails) => {
        // removing condition
        // const remainingWeight = getThreePointNumber(getThreePointNumber(inboundDetails.shipmentWeight) - getThreePointNumber(lineDetails.reduce((linesWeight, line) => {
        //     return line.id !== lineId ? linesWeight + Number(line.netWeight) : Number(linesWeight)
        // }, 0)))

        // if (remainingWeight < Number(newLine.netWeight)) {
        //     return notify(
        //         remainingWeight > 0 ?
        //             `You cannot add Net Weight more than ${remainingWeight}gm` :
        //             'You have no weight remaining to add a new Line',
        //         'error')
        // }
        const lineData: TLineDetails = {
            ...newLine,
            manualEntryMade: manualInput && !buybackAssuredCase(),
            declaredFineness: Number(newLine.declaredFineness),
            declaredWeight: Number(newLine.declaredWeight),
            netWeight: Number(newLine.netWeight),
            serialNumber: newLine.serialNumber,
            metalType: newLine.metalType
        }
        const { error } = await execute(() => createOrUpdateLineDetails({
            globalTransactionNumber: globalTransactionNumber!,
            lineDetails: { ...lineData, id: lineId },
            transactionType
        }))
        if (error === null) {
            notify(lineId ? 'Inbound Line Updated.' : 'Inbound Line Created.')
            closeInboundLine()
        }
    }

    const toggleManualInput = () => {

        if(!manualInput && renderCount >= 2) {
            setFormData(prev => ({...prev, netWeight:'' as unknown as number}))
        }
        
        setField('netWeight', {
            disabledManualInput: !manualInput && !buybackAssuredCase()
        })

        renderCount < 3 && setRenderCount(renderCount + 1)
    }

    useEffect(() => {
        
        let initialValue: TLineDetails = {} as TLineDetails 
        if(transactionType === TRANSACTION_TYPES.GMS || transactionType === TRANSACTION_TYPES.PURITY_TESTING) {
            initialValue = {...initialValue,  productType: LINE_PRODUCT_TYPE.INBOUNDSCRAP} 
            setField('productType', {
                isVisible: true,
                disabled: true
            })
        } else if(transactionType === TRANSACTION_TYPES.BUY_BACK){
            initialValue = {...initialValue, metalType: METAL_TYPES.GOLD} 
            setFormConfig(prev => {
                return [
                    ...prev.map(field => {
                        if (field.fieldName === 'productType') {
                            field.isVisible = true
                            field.validation = { required: true }
                            field.options = field.options?.filter(option =>  [LINE_PRODUCT_TYPE['MMTC-PAMP Product'], LINE_PRODUCT_TYPE.INBOUNDSCRAP].includes(option.value))
                        }
                        if(field.fieldName === 'metalType') {
                            field.isVisible = true
                            field.disabled = false
                        }
                        return field
                    })
                ]
            })
        }
        else if (transactionType === TRANSACTION_TYPES.BUY_SELL || transactionType === TRANSACTION_TYPES.JOB_WORK) {
            setField('productType', {options: [{label: LINE_PRODUCT_TYPE.INBOUNDSCRAP, value: LINE_PRODUCT_TYPE.INBOUNDSCRAP}]})
        }

        if (lineId) {
            initialValue = { ...lineDetails.filter(line => line.id === lineId)[0], ...initialValue }
        }

        if(Object.keys(initialValue).length) setFormData(initialValue)
    }, [])

    useEffect(()=>{
        toggleManualInput()
    },[manualInput, formData.productType])

    useEffect(()=>{
        lineDetails.forEach(line => {
            if(line.manualEntryMade && line.id === lineId) {
                setManualInput(true)
            }
        })
    },[lineDetails])

    return (
        <div style={{ padding: '0 10px' }}>
            <div style={{ borderBottom: '1px solid #d8d8d8' }}>
                <Title style={{ textAlign: 'left', margin: '20px 10px 10px 10px', color: '#7f7d76' }}>Line Details</Title>
            </div>
            <div style={{ padding: '20px 0px' }}>
                <DynamicForm {...registerForm()} onSubmit={showSaveButton ? createInboundLine : undefined} submitText='Save' />
            </div>
            <Divider style={{ marginBottom: '20px' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                {privilages.admin.all && !buybackAssuredCase() ? 
                (manualInput ? 
                    <Button variant='contained' type='button' color='error' onClick={() => setManualInput(prev => !prev)} >Remove Manual Input</Button> :
                    <Button variant='contained' type='button' color='primary' onClick={() => setManualInput(prev => !prev)} >Enable Manual Input</Button> 
                ):
                <></>}
                <Button variant='outlined' type='button' color='secondary' onClick={closeInboundLine} >{'Close'}</Button>
            </div>
        </div>
    )
}

export default InboundLine